import { Directive, EmbeddedViewRef, OnDestroy, TemplateRef, ViewContainerRef, effect, inject, input } from '@angular/core';
import { Subscription, fromEvent, tap } from 'rxjs';

@Directive({
    selector: '[appTooltipPortal]',
})
export class TooltipPortalDirective implements OnDestroy {
    private templateRef = inject(TemplateRef);
    private viewContainerRef = inject(ViewContainerRef);

    readonly appTooltipPortal = input.required<boolean>();
    readonly appTooltipPortalAnchor = input.required<HTMLElement>();

    private embeddedView: EmbeddedViewRef<unknown> | null = null;
    private portalEl: HTMLElement | null = null;
    private scrollSubscription: Subscription | null = null;

    constructor() {
        effect(() => {
            if (this.appTooltipPortal()) {
                this.attach();
            } else {
                this.detach();
            }
        });
    }

    ngOnDestroy(): void {
        this.detach();
    }

    private attach(): void {
        if (this.embeddedView) return;

        this.embeddedView = this.viewContainerRef.createEmbeddedView(this.templateRef);
        this.embeddedView.detectChanges();

        this.portalEl = <HTMLElement>this.embeddedView.rootNodes[0];
        this.portalEl.style.position = 'fixed';
        this.portalEl.style.zIndex = '9999';

        document.body.appendChild(this.portalEl);
        this.updatePosition();

        this.scrollSubscription = fromEvent(window, 'scroll', { capture: true })
            .pipe(tap(() => this.updatePosition()))
            .subscribe();
    }

    private detach(): void {
        this.scrollSubscription?.unsubscribe();
        this.scrollSubscription = null;

        if (this.portalEl) {
            this.portalEl.remove();
            this.portalEl = null;
        }

        if (this.embeddedView) {
            this.embeddedView.destroy();
            this.embeddedView = null;
        }
    }

    private updatePosition(): void {
        if (!this.portalEl) return;

        const anchorRect = this.appTooltipPortalAnchor().getBoundingClientRect();

        this.portalEl.style.left = `${anchorRect.left + anchorRect.width / 2}px`;
        this.portalEl.style.bottom = `${window.innerHeight - anchorRect.top}px`;
        this.portalEl.style.transform = 'translateX(-50%)';
    }
}
